import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  X,
  Plus,
  PushPin,
  Flag,
  MagnifyingGlass,
  Eraser,
  CircleNotch,
  ArrowCounterClockwise,
  Archive,
  CaretDown,
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { graphKeys, workBoardKeys, meKeys } from '~/lib/query-keys';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { Label } from '~/components/ui/label';
import { Badge } from '~/components/ui/badge';
import { Skeleton } from '~/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, useTabPanel } from '~/components/ui/tabs';
import { ActionBar } from '~/components/ui/action-bar';
import { useConfirm } from '~/components/confirm-dialog';
import { toast } from 'sonner';
import {
  useGraphEntity,
  type GraphEntity,
  type GraphEntityDetail,
  type EntityAttributeRow,
  type EntityRelationshipRow,
} from '~/hooks/queries/use-graph';
import { useArchiveEntity, useDeleteEntity } from '~/hooks/mutations/use-graph-mutations';
import { useCurrentUser } from '~/hooks/queries/use-is-admin';
import { isOwnPersonEntry } from './entity-selection';
import { buildDeleteEntityConfirmCopy } from './entity-delete-copy';
import { fireEntitySaveToast } from '~/lib/entity-save-outcome';
import { buildEntityResearchAsk, type ResearchDepth } from './research-ask';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';

interface EntityDetailPanelProps {
  entity: GraphEntity;
  onClose: () => void;
  /**
   * FRONT-525: open the chat that produced a fact ("pull more from this chat").
   * Wired by the Memory route, which mounts the chat modal. Absent → the
   * provenance session shows as plain text (no jump-back).
   */
  onOpenSourceChat?: (sessionId: string, title: string) => void;
  /**
   * FRONT-752: start a BRAND-NEW chat over this page with `ask` as its first
   * message — what the Research menu does. Required, not optional: a mount
   * site that forgot it would render a Research menu that silently does
   * nothing, and the type checker is the only thing that can catch that.
   */
  onStartResearchChat: (ask: string) => void;
}

type PanelTab = 'overview' | 'relationships' | 'provenance';

/** Human label for a provenance surface (FRONT-377). */
function surfaceLabel(surface: string): string {
  switch (surface) {
    case 'chat':
      return 'a chat';
    case 'document':
      return 'a document chat';
    case 'voice':
      return 'a voice call';
    case 'onboarding':
      return 'onboarding';
    case 'agent':
      return 'an agent';
    case 'skill':
      return 'a skill run';
    case 'reflect':
      return 'a reflection';
    default:
      return surface;
  }
}

/**
 * One fact's origin, as shown on the Provenance tab: how it was learned, when,
 * the fact itself, and — FRONT-617 — which document it was read out of.
 *
 * Its own component so it can be rendered and asserted on directly. The tab it
 * lives on is only reachable by clicking, and the frontend tests render to
 * static markup with no DOM to click in, so inlined it was guarded solely by a
 * browser spec that the required CI checks never run.
 */
export function AttributeOriginRow({ attribute }: { attribute: EntityAttributeRow }) {
  return (
    <div className="flex flex-col text-xs">
      <div className="flex items-baseline gap-2">
        <span className="text-muted-foreground truncate">
          {attribute.provenance?.surface
            ? surfaceLabel(attribute.provenance.surface)
            : attribute.source || 'unknown source'}
        </span>
        <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/70 shrink-0">
          {relativeTime(attribute.learned_at)}
        </span>
      </div>
      <div className="font-mono text-foreground break-words">
        {attribute.category}.{attribute.key} ={' '}
        <span className="text-muted-foreground">"{attribute.value}"</span>
      </div>
      {/* The document this specific fact came out of. Plain text, not a link —
          the pointer is a filename and the graph holds no document id to
          navigate to. */}
      {attribute.source_document ? (
        <div className="text-muted-foreground break-words">
          from <span className="text-foreground">{attribute.source_document}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * FRONT-1397: the Overview tab's in-place edit draft — name, "also known as"
 * names, and facts, all edited locally and sent together on one Save. Kept as
 * plain, pure-function-driven data (rather than folding the transforms inline
 * into the component) for two reasons: it mirrors `work-detail.tsx`'s
 * `FactRowsEditor`/`usePristineFactsSync` precedent for an editable fact-row
 * list, and — this repo's frontend tests render via `renderToStaticMarkup`
 * with no jsdom/testing-library, so a real "type then click" interaction
 * can't be simulated — every transform the UI triggers is directly
 * unit-testable by calling the exact function the handler calls, without
 * needing a live DOM.
 */
export interface FactDraftRow {
  /** Client-only stable id for React's list key + row identity. Never sent. */
  uid: string;
  category: string;
  key: string;
  value: string;
}

export interface EntityDraft {
  name: string;
  aliasNames: string[];
  facts: FactDraftRow[];
}

/**
 * FRONT-1397 fix (reviewer finding): the server returns attributes in no
 * particular order, so an un-sorted seed reshuffled the fact list on every
 * save/reload. Sort by category then key (plain code-point order, not
 * locale-aware collation — deterministic across environments) so the list is
 * stable. Only the SEED is sorted; a row the user adds afterward
 * (`addFactRow`) still goes straight to the end until the next save reseeds.
 */
function compareFactSeed(a: EntityAttributeRow, b: EntityAttributeRow): number {
  if (a.category !== b.category) return a.category < b.category ? -1 : 1;
  if (a.key !== b.key) return a.key < b.key ? -1 : 1;
  return 0;
}

/** Seed a draft from the fetched entity-detail payload (the server's truth). */
export function draftFromDetail(detail: GraphEntityDetail): EntityDraft {
  const sortedAttributes = [...(detail.attributes ?? [])].sort(compareFactSeed);
  return {
    name: detail.entity.name,
    aliasNames: (detail.aliases ?? []).map((a) => a.name),
    facts: sortedAttributes.map((a) => ({
      uid: crypto.randomUUID(),
      category: a.category,
      key: a.key,
      value: a.value,
    })),
  };
}

/**
 * Refuse (quietly — the caller disables the Add control rather than showing
 * an error) an empty name, a case-insensitive match on the entry's own
 * current (draft) name, or a case-insensitive duplicate of a chip already in
 * the draft.
 */
export function isAliasAddDisabled(candidate: string, currentName: string, existing: string[]): boolean {
  const trimmed = candidate.trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  if (lower === currentName.trim().toLowerCase()) return true;
  return existing.some((a) => a.toLowerCase() === lower);
}

export function addAliasName(existing: string[], candidate: string): string[] {
  const trimmed = candidate.trim();
  if (!trimmed) return existing;
  return [...existing, trimmed];
}

export function removeAliasName(existing: string[], name: string): string[] {
  return existing.filter((a) => a !== name);
}

export function addFactRow(facts: FactDraftRow[]): FactDraftRow[] {
  return [...facts, { uid: crypto.randomUUID(), category: '', key: '', value: '' }];
}

export function removeFactRow(facts: FactDraftRow[], uid: string): FactDraftRow[] {
  return facts.filter((f) => f.uid !== uid);
}

export function updateFactRow(
  facts: FactDraftRow[],
  uid: string,
  patch: Partial<Pick<FactDraftRow, 'category' | 'key' | 'value'>>,
): FactDraftRow[] {
  return facts.map((f) => (f.uid === uid ? { ...f, ...patch } : f));
}

export interface FactRowErrors {
  category?: string;
  key?: string;
  value?: string;
}

/**
 * FRONT-1397 fix (reviewer finding, BLOCKER): the old validation required
 * category and key to match `[A-Za-z0-9_-]+`, but that is stricter than what
 * the storage layer actually needs. A Work container's context facts are
 * stored with the LABEL as the attribute key
 * (`setAttribute(id, "context", fact.label, …)` in `src/work/plan-routes.ts`
 * and `src/graph/routes.ts`'s `/context` route) — "Kickoff date" or "Sarah's
 * salary band" are entirely ordinary keys with only a length cap server-side.
 * The old rule turned opening any such entry into an unfixable validation
 * error that disabled Save for the WHOLE card, including the Name field.
 *
 * The only rule the server actually needs: the composite key is stored as
 * `${category}.${key}` and split back apart on the FIRST dot. A dot in the
 * category would silently re-slice the pair, so that's still refused; a dot
 * in the KEY is fine — the remainder-after-first-dot round-trips exactly.
 * Category, key and value are otherwise free text (any script, punctuation,
 * or whitespace), required and non-empty. The same category+key pair (exact,
 * case-sensitive match — unchanged) may not repeat across rows.
 */
export function validateFactRows(facts: FactDraftRow[]): Map<string, FactRowErrors> {
  const errors = new Map<string, FactRowErrors>();
  const seen = new Map<string, string[]>();

  for (const row of facts) {
    const rowErrors: FactRowErrors = {};
    const category = row.category.trim();
    const key = row.key.trim();

    if (!category) rowErrors.category = 'Category is required';
    else if (category.includes('.')) rowErrors.category = "Category can't contain a dot";

    if (!key) rowErrors.key = 'Key is required';

    if (!row.value.trim()) rowErrors.value = 'Value is required';

    if (!rowErrors.category && !rowErrors.key) {
      const compound = `${category}.${key}`;
      const uids = seen.get(compound) ?? [];
      uids.push(row.uid);
      seen.set(compound, uids);
    }

    errors.set(row.uid, rowErrors);
  }

  for (const uids of seen.values()) {
    if (uids.length <= 1) continue;
    for (const uid of uids) {
      errors.set(uid, { ...errors.get(uid), key: `Duplicate category + key` });
    }
  }

  return errors;
}

export function factRowHasError(errors: FactRowErrors | undefined): boolean {
  return !!errors && !!(errors.category || errors.key || errors.value);
}

/**
 * FRONT-1397 fix (reviewer finding): the old read-only Overview grouped facts
 * by category and showed each one's confidence — the signal that tells "Eddy
 * worked this out" apart from "you told me this". The editable rows dropped
 * it. Matches on the row's current category+key against the fetched detail's
 * attribute list (never the row's `value`, which the user may have already
 * edited): a brand-new row (blank category/key, or a category+key that
 * doesn't match anything saved) has no known confidence and this returns
 * `undefined`. A row whose VALUE was edited but whose category+key are
 * unchanged still matches and keeps showing its stored confidence — that's
 * correct, it describes what's currently saved, not the unsaved draft.
 */
export function findFactConfidence(
  row: FactDraftRow,
  attributes: EntityAttributeRow[],
): 'low' | 'medium' | 'high' | undefined {
  const category = row.category.trim();
  const key = row.key.trim();
  if (!category || !key) return undefined;
  return attributes.find((a) => a.category === category && a.key === key)?.confidence;
}

/**
 * FRONT-1397 fix (reviewer finding): the fact remove control's accessible
 * name used to be positional (`Remove fact row ${i + 1}`), which tells a
 * screen-reader user nothing about which fact and shifts every time a row is
 * deleted. Names the fact itself instead — same precedent as
 * `work-detail.tsx`'s `FactRowsEditor` Remove control (`Remove ${row.label ||
 * 'fact'}`) — falling back to whichever of category/key is present, or the
 * plain word "fact" while the row is still empty.
 */
export function factRowName(row: FactDraftRow): string {
  const category = row.category.trim();
  const key = row.key.trim();
  if (category && key) return `${category}.${key}`;
  return category || key || 'fact';
}

/**
 * FRONT-1397 fix (reviewer finding): a value with an embedded line break (a
 * Work container's notes are stored as one long multi-line fact value) or
 * long enough that a single-line field would hide most of it gets a
 * multi-line control instead — an ordinary single-line `Input` silently
 * strips line breaks the moment it's touched, and the first keystroke would
 * feed the flattened string straight back into the draft.
 */
const MULTILINE_VALUE_THRESHOLD = 80;
export function isMultilineValue(value: string): boolean {
  return value.includes('\n') || value.length > MULTILINE_VALUE_THRESHOLD;
}

/**
 * The exact `PUT /api/graph/entity/:id` body Save sends.
 *
 * FRONT-1397 fix (BLOCKER): `attributes`/`aliases` are a REPLACE-WHOLE-LIST on
 * the server — an omitted fact is deleted. `seeded` says whether the draft was
 * actually populated from the server's real fact/alias lists (vs. still the
 * blank placeholder shown before `useGraphEntity` resolves, or after a failed
 * fetch); when it's false, both keys are left out of the body entirely so the
 * route's own "omitted key = leave untouched" contract does the safe thing —
 * a rename can still go through without silently wiping every fact and alias.
 * Defaults to `true` (existing callers, and the normal path once the UI's own
 * loading gate has let a save happen at all, always have a seeded draft).
 */
export function buildSavePayload(
  draft: EntityDraft,
  seeded: boolean = true,
): {
  name: string;
  attributes?: Record<string, string>;
  aliases?: string[];
} {
  const name = draft.name.trim();
  if (!seeded) return { name };
  const attributes: Record<string, string> = {};
  for (const row of draft.facts) {
    const category = row.category.trim();
    const key = row.key.trim();
    attributes[`${category}.${key}`] = row.value.trim();
  }
  return { name, attributes, aliases: draft.aliasNames };
}

export function draftsEqual(a: EntityDraft, b: EntityDraft): boolean {
  if (a.name !== b.name) return false;
  if (a.aliasNames.length !== b.aliasNames.length) return false;
  for (let i = 0; i < a.aliasNames.length; i++) {
    if (a.aliasNames[i] !== b.aliasNames[i]) return false;
  }
  if (a.facts.length !== b.facts.length) return false;
  for (let i = 0; i < a.facts.length; i++) {
    const fa = a.facts[i];
    const fb = b.facts[i];
    if (fa.category !== fb.category || fa.key !== fb.key || fa.value !== fb.value) return false;
  }
  return true;
}

function blankDraft(name: string): EntityDraft {
  return { name, aliasNames: [], facts: [] };
}

/** Draft seed to use on first render: the fetched detail if it's already
 * available (and matches the entity being shown), otherwise a blank draft
 * that falls back to the (possibly stub) `entity` prop's name. */
function initialDraftFor(entity: GraphEntity, detail: GraphEntityDetail | undefined): EntityDraft {
  if (detail && detail.entity.id === entity.id) return draftFromDetail(detail);
  return blankDraft(entity.name);
}

export interface DraftReseedResult {
  /** Always the server's latest content — mirrors `use-pristine-facts-sync.ts`'s
   *  `pristineFacts`, which re-seeds unconditionally on a real content change. */
  baseline: EntityDraft;
  /** The rendered draft to adopt, or `null` to leave whatever the user is
   *  currently editing untouched. */
  draft: EntityDraft | null;
}

/**
 * FRONT-1397 fix (MAJOR): the pure decision behind the draft/baseline re-seed
 * effect — a fact written elsewhere while this panel is open (chat, reflect,
 * a merge) must not be silently deleted by the next Save. Extracted as a
 * standalone function because this repo's frontend tests render via
 * `renderToStaticMarkup`, which never runs `useEffect` — this is the
 * directly-testable proof that the rule the effect implements is correct.
 *
 * Mirrors `frontend/src/features/work/use-pristine-facts-sync.ts`: the
 * baseline always tracks the server's latest known-good content once it's
 * changed (so Discard reflects reality, and a background reseed is safe to
 * apply once the user saves or discards); the rendered draft only follows
 * along when there is nothing unsaved to protect. A genuinely different
 * entity (`isSameEntity: false` — the panel just switched) always adopts the
 * server content regardless of `isDirty`, since any leftover dirty flag at
 * that point belongs to the entity being left, not the one arriving.
 */
export function resolveDraftReseed(
  serverDraft: EntityDraft,
  isSameEntity: boolean,
  isDirty: boolean,
): DraftReseedResult {
  if (!isSameEntity || !isDirty) return { baseline: serverDraft, draft: serverDraft };
  return { baseline: serverDraft, draft: null };
}

/**
 * FRONT-1397 fix (MAJOR + BLOCKER): whether a save response is safe to settle
 * onto the draft/baseline currently on screen.
 *
 * Fix 4: `useMutation`'s `onSuccess` always runs against the LATEST render's
 * closure, not the one bound when `.mutate()` fired — so a save kicked off for
 * entity A whose response lands after the panel has moved on to entity B would
 * otherwise settle A's name/facts/aliases onto B's draft. Comparing the
 * response's own entity id against whichever entity is on screen now catches
 * that; cache invalidation and the toast still fire unconditionally; only the
 * draft/baseline settlement is gated by this.
 *
 * Fix 7: also doubles as the guard against the entry having vanished between
 * write and read-back (`data.entity` absent) — `draftFromDetail` dereferences
 * `.entity.name` unguarded, so settling only when `data.entity` is genuinely
 * present avoids that crash too.
 */
export function isSaveResponseForCurrentEntity(
  data: { entity?: { id: string } | null } | null | undefined,
  currentEntityId: string,
): boolean {
  return !!data?.entity && data.entity.id === currentEntityId;
}

export function EntityDetailPanel({
  entity,
  onClose,
  onOpenSourceChat,
  onStartResearchChat,
}: EntityDetailPanelProps) {
  const [tab, setTab] = useState<PanelTab>('overview');
  const tabPanel = useTabPanel(tab);
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { data: detail, isLoading: detailIsLoading, isError: detailIsError } = useGraphEntity(entity.id);
  const attributes: EntityAttributeRow[] = detail?.attributes ?? [];
  const relationships: EntityRelationshipRow[] = detail?.relationships ?? [];
  const liveEntity = (detail?.entity ?? entity) as GraphEntity & {
    pinned?: boolean;
    flagged?: boolean;
  };
  const isPinned = !!liveEntity.pinned;
  const isFlagged = !!liveEntity.flagged;

  // FRONT-1397 fix (BLOCKER): has the draft actually been populated from the
  // server's real fact/alias lists? Until this is true, `draft` is still the
  // blank placeholder (real name, but EMPTY facts/aliases) — editable, but
  // never safe to save: a rename fired from that state would submit a
  // present-but-empty `attributes`/`aliases`, which the server reads as
  // "delete everything". Drives both halves of the fix: the Overview tab
  // shows a loading/error state instead of the editable fields until this is
  // true (below), and `buildSavePayload` is only ever called with it as a
  // second layer of defense.
  const seeded = !!detail && detail.entity.id === entity.id && !detailIsLoading;

  // FRONT-1397: the Overview tab's whole edit surface — name, "also known
  // as", facts — as one draft, saved together with a single Save. `draft` is
  // what's rendered/edited; `baseline` is the last value known to match the
  // server (used for the dirty comparison and for Discard). Both are seeded
  // lazily from whatever `detail` is already available at mount — including
  // synchronously in tests, which never run effects — so a fresh panel never
  // shows as dirty.
  const [draft, setDraft] = useState<EntityDraft>(() => initialDraftFor(entity, detail));
  const [baseline, setBaseline] = useState<EntityDraft>(() => initialDraftFor(entity, detail));
  const [aliasInput, setAliasInput] = useState('');
  // FRONT-1397 fix (MAJOR): the last server-derived draft this panel actually
  // saw, keyed by entity id — used by the re-seed effect below to tell "the
  // server's content genuinely changed" apart from "the same refetch settled
  // again with the same content", and "this is the same entity" apart from
  // "the panel just switched to a different one" (`resolveDraftReseed`).
  const lastServerDraftRef = useRef<{ entityId: string; draft: EntityDraft } | null>(
    detail && detail.entity.id === entity.id ? { entityId: entity.id, draft: draftFromDetail(detail) } : null,
  );

  // Reset tab + draft back to a blank slate when the selected entity changes
  // — PA-style (each new entity opens fresh). Deliberately keyed on
  // `entity.id` alone (not `entity.name`): the `entity` prop is a snapshot
  // the Memory list handed the panel on click, and a background list refresh
  // updating that snapshot's `name` for the SAME id must not wipe an
  // in-progress edit — the detail-seed effect below is the only thing
  // allowed to update the draft for an entity already open.
  useEffect(() => {
    setTab('overview');
    setAliasInput('');
    const seeded = blankDraft(entity.name);
    setDraft(seeded);
    setBaseline(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.id]);

  const dirty = useMemo(() => !draftsEqual(draft, baseline), [draft, baseline]);
  // Read inside the re-seed effect below without needing `dirty` in its own
  // dependency array (which would defeat the "only reacts to a real server
  // content change" guard) — a plain per-render assignment, not an effect.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // FRONT-1397 fix (MAJOR): re-seed the draft/baseline from the fetched
  // detail whenever the server's content actually changed — a fact written
  // elsewhere while this panel is open (chat, reflect, a merge) must not be
  // silently deleted by the next Save. Mirrors
  // `use-pristine-facts-sync.ts`: the baseline always tracks the server's
  // latest content once it's changed; the rendered draft only follows along
  // when the user has no unsaved changes (`resolveDraftReseed`). Replaces the
  // old "seed exactly once per entity, ignore every later refetch" guard,
  // which is what let a background refetch's fresher content sit in the
  // cache while the rendered rows (and the next Save's payload) stayed stale.
  useEffect(() => {
    if (!detail || detail.entity.id !== entity.id) return;
    const serverDraft = draftFromDetail(detail);
    const prev = lastServerDraftRef.current;
    const isSameEntity = prev?.entityId === entity.id;
    if (isSameEntity && draftsEqual(prev!.draft, serverDraft)) return;
    lastServerDraftRef.current = { entityId: entity.id, draft: serverDraft };
    const result = resolveDraftReseed(serverDraft, isSameEntity, dirtyRef.current);
    if (result.draft) setDraft(result.draft);
    setBaseline(result.baseline);
  }, [detail, entity.id]);

  const factErrors = useMemo(() => validateFactRows(draft.facts), [draft.facts]);
  const hasFactErrors = useMemo(
    () => draft.facts.some((f) => factRowHasError(factErrors.get(f.uid))),
    [draft.facts, factErrors],
  );
  // FRONT-1397 fix: an empty (or whitespace-only) name gives inline feedback
  // and disables Save rather than reaching the server and bouncing back as a
  // raw error toast.
  const nameEmpty = !draft.name.trim();
  const aliasAddDisabled = isAliasAddDisabled(aliasInput, draft.name, draft.aliasNames);

  function handleAddAlias() {
    if (isAliasAddDisabled(aliasInput, draft.name, draft.aliasNames)) return;
    setDraft((d) => ({ ...d, aliasNames: addAliasName(d.aliasNames, aliasInput) }));
    setAliasInput('');
  }
  function handleRemoveAlias(name: string) {
    setDraft((d) => ({ ...d, aliasNames: removeAliasName(d.aliasNames, name) }));
  }
  function handleAddFactRow() {
    setDraft((d) => ({ ...d, facts: addFactRow(d.facts) }));
  }
  function handleRemoveFactRow(uid: string) {
    setDraft((d) => ({ ...d, facts: removeFactRow(d.facts, uid) }));
  }
  function handleUpdateFactRow(uid: string, patch: Partial<Pick<FactDraftRow, 'category' | 'key' | 'value'>>) {
    setDraft((d) => ({ ...d, facts: updateFactRow(d.facts, uid, patch) }));
  }

  const maxConfidence = useMemo<'low' | 'medium' | 'high' | null>(() => {
    let best: 'low' | 'medium' | null = null;
    for (const a of attributes) {
      if (a.confidence === 'high') return 'high';
      if (a.confidence === 'medium') best = 'medium';
      else if (a.confidence === 'low' && best !== 'medium') best = 'low';
    }
    return best;
  }, [attributes]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      return api.put<GraphEntityDetail>(
        `/api/graph/entity/${encodeURIComponent(entity.id)}`,
        // FRONT-1397 fix (BLOCKER): defense-in-depth alongside the UI's own
        // loading/error gate below — `buildSavePayload` never sends
        // `attributes`/`aliases` unless `seeded` is true, so even a save fired
        // before the entry has actually loaded can only ever rename, never
        // wipe every fact and alias.
        buildSavePayload(draft, seeded),
      );
    },
    // FRONT-1215: capture whether the entry was Inactive BEFORE the write, at
    // fire time. Reading it in `onSuccess` off the rendered entity would race
    // the refetch this mutation's own invalidation triggers; `onMutate`'s
    // context is the race-free place for a before/after comparison.
    onMutate: () => ({ wasArchivedBefore: !!liveEntity.archived_at }),
    onSuccess: (data, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: graphKeys.all });
      // FRONT-1154 review fix: a project rename here (this panel edits the
      // same graph entities the Work board renders) left the
      // board showing the old name — `useRenameEntity` already invalidates
      // both caches, this mutation should too.
      queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
      // FRONT-857 review fix: this panel also edits the caller's own
      // person-type self-entity (id === the user's Zitadel id) — the same
      // row the home greeting's preferred name is read from. Without this,
      // renaming yourself here left the greeting showing the stale name for
      // the rest of the session (staleTime: Infinity on that read).
      queryClient.invalidateQueries({ queryKey: meKeys.onboarded() });
      // FRONT-1397 fix (MAJOR + BLOCKER): settle the draft on the server's
      // response rather than leaving a stale local one — but only when the
      // response is actually FOR the entity currently on screen.
      // `useMutation`'s `onSuccess` always runs against the latest render's
      // closure, not the one bound when Save fired — clicking Save on entity
      // A, then opening entity B before A's request resolves, would
      // otherwise settle A's content onto B's draft the moment A's response
      // lands. Cache invalidation and the toast above/below still fire
      // unconditionally — only this settlement is guarded. Also covers the
      // entry having vanished between write and read-back (`data.entity`
      // absent), which `draftFromDetail` would otherwise dereference unguarded.
      if (isSaveResponseForCurrentEntity(data, entity.id)) {
        const settled = draftFromDetail(data);
        lastServerDraftRef.current = { entityId: entity.id, draft: settled };
        setDraft(settled);
        setBaseline(settled);
      }
      // FRONT-1109: the write always landed here — `stayedArchivedNote`
      // (when present) only means the entity's implicit un-archive was
      // withheld because an active duplicate exists elsewhere. That's a
      // "different than you expected", not a failure, so it's a warning
      // toast (amber), never the destructive/error variant.
      //
      // FRONT-1215: the mirror case matters here too, now that the Memory
      // page's Inactive view (FRONT-1155) makes an Inactive entry openable at
      // all. Saving one brings it back to Active — that IS the intended rule
      // (a write is proof the entry is live again) — but a bare "Saved" left
      // the user with an entry that quietly rejoined Eddy's working set and
      // vanished from the list they were looking at. Especially confusing
      // sitting a few rows above an explicit Reactivate button.
      fireEntitySaveToast(data, undefined, context?.wasArchivedBefore);
    },
    onError: (err) => toast.error((err as Error).message),
  });

  function handleSave() {
    if (hasFactErrors || nameEmpty) return;
    updateMutation.mutate();
  }
  function handleDiscard() {
    setDraft(baseline);
    setAliasInput('');
  }

  // FRONT-1019: reuse the shared delete-entity mutation (Work board's
  // create/archive/delete hooks) instead of hand-rolling one here — it
  // already shows the "Moved to Recycle bin" toast with a working Undo action and
  // invalidates the graph + Work-board caches.
  const deleteEntity = useDeleteEntity();
  // FRONT-796: the user's own person entry (id === their Zitadel user id) is
  // never trashable on the backend, so Forget is not offered on it.
  const { userId: currentUserId } = useCurrentUser();
  // Fails closed: while the signed-in id is unknown, Forget stays hidden.
  const hideForget = !currentUserId || isOwnPersonEntry(entity.id, currentUserId);

  // FRONT-1155: Reactivate — the detail panel's primary path back to Active
  // for an archived entity (the row's hover icon is the secondary path). Same
  // mutation the Work board's card menu uses; already toasts "Reactivated".
  const archiveEntity = useArchiveEntity();

  // Optimistic pin/flag: snapshot the entity cache before writing the
  // optimistic value so onError can restore it. Without the snapshot,
  // a network hiccup left the UI showing the wrong state until the
  // next graph refetch. onSuccess narrows invalidation to this entity
  // + the activity feed instead of evicting the whole graph cache.
  type EntityCache = { entity?: GraphEntity & { pinned?: boolean; flagged?: boolean } } | undefined;
  const entityKey = graphKeys.entity(entity.id);

  const pinMutation = useMutation({
    mutationFn: (next: boolean) =>
      api.post(`/api/graph/entity/${encodeURIComponent(entity.id)}/pin`, { pinned: next }),
    onMutate: (next: boolean) => {
      const previous = queryClient.getQueryData<EntityCache>(entityKey);
      queryClient.setQueryData<EntityCache>(entityKey, (prev) =>
        prev?.entity ? { ...prev, entity: { ...prev.entity, pinned: next } } : prev,
      );
      return { previous };
    },
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({ queryKey: entityKey });
      queryClient.invalidateQueries({ queryKey: graphKeys.activity() });
      // Pinned entities lead the Memory list, so a pin moves the row.
      queryClient.invalidateQueries({ queryKey: [...graphKeys.all, 'entities'] });
      toast.success(next ? 'Pinned' : 'Unpinned');
    },
    onError: (err, _next, ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData(entityKey, ctx.previous);
      toast.error(`Pin failed: ${(err as Error).message}`);
    },
  });

  const flagMutation = useMutation({
    mutationFn: (next: boolean) =>
      api.post(`/api/graph/entity/${encodeURIComponent(entity.id)}/flag`, { flagged: next }),
    onMutate: (next: boolean) => {
      const previous = queryClient.getQueryData<EntityCache>(entityKey);
      queryClient.setQueryData<EntityCache>(entityKey, (prev) =>
        prev?.entity ? { ...prev, entity: { ...prev.entity, flagged: next } } : prev,
      );
      return { previous };
    },
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({ queryKey: entityKey });
      queryClient.invalidateQueries({ queryKey: graphKeys.activity() });
      toast.success(next ? 'Flagged' : 'Unflagged');
    },
    onError: (err, _next, ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData(entityKey, ctx.previous);
      toast.error(`Flag failed: ${(err as Error).message}`);
    },
  });

  /**
   * FRONT-752: Research offers the two depths Eddy actually has, and the chosen
   * one STARTS. The ask goes straight into a fresh chat opened over this page —
   * it used to be parked in the composer on another page, which cost a second
   * press of Send and threw away the Memory view the user was standing in.
   */
  function handleResearch(depth: ResearchDepth) {
    // `liveEntity`, never the `entity` prop — same reason the header reads it
    // (FRONT-1155 Fix 3), and it matters more here than anywhere else on this
    // panel: the ask is SENT, with no composer step where a user could notice
    // it is wrong. The prop is a snapshot the Memory route took on click and
    // never re-syncs, so it is empty for an entity opened from a deep link or
    // the command palette (the route hands the panel an id-only stub and lets
    // its own fetch fill in the rest) — which would send Eddy a raw UUID to
    // research — and stale after a rename in this very panel.
    onStartResearchChat(buildEntityResearchAsk(liveEntity.name || liveEntity.id, depth));
  }

  async function handleDelete() {
    const copy = buildDeleteEntityConfirmCopy(
      // Same read as above. Still fronted by a confirm dialog, so a stale name
      // here degrades gracefully rather than silently — normalized anyway so
      // the two paths can't drift.
      liveEntity.name || liveEntity.id,
      detail ? relationships.length : undefined,
    );
    const ok = await confirm({
      title: copy.title,
      description: copy.description,
      confirmText: 'Delete',
      destructive: true,
    });
    if (ok) deleteEntity.mutate(entity.id, { onSuccess: onClose });
  }

  const tabs: Array<{ id: PanelTab; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'relationships', label: 'Relationships' },
    { id: 'provenance', label: 'Provenance' },
  ];

  // FRONT-1399: `self-start` below is load-bearing. This panel is a grid item
  // in the Memory page's two-column layout, and CSS Grid's default
  // `align-items: stretch` sized it to the entity list beside it — on a short
  // entry that left a tall empty column and pushed the action row (Research,
  // Pin, Flag, …) to the very bottom of the page, past the whole list. Opting
  // this item out of the stretch keeps the panel at its own content height
  // wherever it is mounted, rather than relying on every future parent
  // remembering to pass `items-start`.
  return (
    <aside
      data-testid="entity-detail-panel"
      className="bg-card border border-border rounded-lg flex flex-col self-start"
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            {/* FRONT-1155 pre-ship review, Fix 3: read from `liveEntity`, not the
                `entity` prop — a deep-linked entity that had to be opened from a
                stub (id-only, resolved outside the loaded list) has no real
                name/type on the prop itself; `liveEntity` picks up the panel's
                own by-id fetch once it resolves, same as the Inactive badge and
                pin/flag indicators below already do. */}
            <span className="font-semibold text-foreground truncate">{liveEntity.name || liveEntity.id}</span>
            <Badge variant="secondary" className="bg-chart-2/15 text-chart-2 text-[length:var(--text-2xs)]">{liveEntity.type}</Badge>
            {liveEntity.archived_at && (
              <Badge variant="secondary" className="text-[length:var(--text-2xs)]">Inactive</Badge>
            )}
            {isPinned && (
              <PushPin size={12} weight="fill" className="text-primary shrink-0" aria-label="Pinned" />
            )}
            {isFlagged && (
              <Flag size={12} weight="fill" className="text-destructive shrink-0" aria-label="Flagged" />
            )}
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground shrink-0"
            aria-label="Close"
            tooltip="Close"
          >
            <X size={14} />
          </Button>
        </div>
        <div className="text-[length:var(--text-2xs)] text-muted-foreground truncate">
          ID: <span className="font-mono">{entity.id}</span>
          {liveEntity.source && (
            <> · from {liveEntity.source}</>
          )}
        </div>
      </div>

      {/* Confidence + meta strip */}
      <div className="px-4 py-2 border-b border-border text-[length:var(--text-2xs)] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {(() => {
          const parts: ReactNode[] = [];
          if (maxConfidence) {
            parts.push(
              <span key="conf">
                Max confidence:{' '}
                <span className={confidenceColor(maxConfidence)}>{capitalize(maxConfidence)}</span>
              </span>,
            );
          }
          parts.push(
            <span key="facts">
              {attributes.length} fact{attributes.length === 1 ? '' : 's'}
            </span>,
          );
          if (liveEntity.updated_at) {
            parts.push(
              <span key="updated">updated {relativeTime(liveEntity.updated_at)}</span>,
            );
          }
          return parts.flatMap((node, i) =>
            i === 0
              ? [node]
              : [<span key={`sep-${i}`} aria-hidden>·</span>, node],
          );
        })()}
      </div>

      {/* Tab strip. FRONT-2485: the shared Tabs look, `sm` because it is a
          side panel's own row, not a page's (was soft blue tabs). */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as PanelTab)} className="px-4 py-2 border-b border-border">
        <TabsList size="sm">
          {tabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="flex-none" {...tabPanel.trigger(t.id)}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Body */}
      <div {...tabPanel.panel} className="p-4 flex-1">
        {tab === 'overview' && (
          <div className="flex flex-col gap-4">
            {!seeded ? (
              detailIsError ? (
                // FRONT-1397 fix (BLOCKER): a failed detail fetch used to leave
                // `detail` undefined forever while still rendering a fully
                // editable (and dirty-able) blank draft — every save from that
                // state nuked the entry's facts and aliases. Plain, non-technical
                // copy; no editable fields at all until the entry actually loads.
                <p className="text-xs text-muted-foreground">
                  This entry couldn't be loaded right now. Close and reopen it to try again.
                </p>
              ) : (
                // FRONT-1397 fix (BLOCKER): a brief loading state instead of an
                // editable, dirty-able surface for an entry that hasn't loaded
                // yet — the real Name/Also known as/Facts fields only appear
                // once `detail` has actually arrived for THIS entity.
                <div className="flex flex-col gap-4" aria-label="Loading entry details">
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-3 w-10" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-3 w-14" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                </div>
              )
            ) : (
              <>
                {/* Name — always editable, clearly labelled. No pencil, no edit
                    mode: this used to be reachable only by clicking a small
                    unlabelled edit icon, which nobody found (FRONT-1397). */}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="entity-detail-name" className="text-xs font-semibold text-muted-foreground">
                    Name
                  </Label>
                  <Input
                    id="entity-detail-name"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Entity name"
                    aria-invalid={nameEmpty}
                  />
                  {nameEmpty && <p className="text-[length:var(--text-2xs)] text-destructive">Name is required.</p>}
                </div>

                {/* Also known as — always shown, even with zero entries, so the
                    add affordance is discoverable. Adds/removes are draft-local;
                    nothing is written until Save. */}
                <div className="flex flex-col gap-1.5">
                  <h4 className="text-xs font-semibold text-muted-foreground">Also known as</h4>
                  {draft.aliasNames.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {draft.aliasNames.map((alias, i) => (
                        <div
                          key={`${alias}-${i}`}
                          className="flex items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 pl-2.5 pr-1 text-xs text-foreground"
                        >
                          <span className="max-w-[160px] truncate">{alias}</span>
                          <Button
                            onClick={() => handleRemoveAlias(alias)}
                            variant="ghost"
                            size="icon-xs"
                            className="size-4 text-muted-foreground hover:text-destructive"
                            aria-label={`Remove "${alias}" from also known as`}
                            tooltip="Remove"
                          >
                            <X size={10} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      value={aliasInput}
                      onChange={(e) => setAliasInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddAlias();
                        }
                      }}
                      placeholder="Add a name…"
                      aria-label="Add another name"
                      className="h-8 flex-1 text-xs"
                    />
                    <Button size="xs" variant="ghost" onClick={handleAddAlias} disabled={aliasAddDisabled}>
                      <Plus size={14} /> Add
                    </Button>
                  </div>
                  <p className="text-[length:var(--text-2xs)] text-muted-foreground">
                    Other names this entry is also known by.
                  </p>
                </div>

                {/* Facts — one row per fact (Category, Key, Value + Remove),
                    replacing the old `category.key = value` textarea. */}
                <div className="flex flex-col gap-1.5">
                  <h4 className="text-xs font-semibold text-muted-foreground">Facts</h4>
                  {draft.facts.length === 0 && (
                    <p className="text-xs text-muted-foreground">No facts yet.</p>
                  )}
                  <div className="flex flex-col gap-2">
                    {draft.facts.map((row) => {
                      const rowErrors = factErrors.get(row.uid);
                      const rowMessage = rowErrors?.category || rowErrors?.key || rowErrors?.value;
                      const confidence = findFactConfidence(row, attributes);
                      const confidenceMarker = confidence && (
                        // FRONT-1397 fix: the confidence signal the old
                        // read-only Overview showed per fact, restored as a
                        // small muted marker beside the row — not inside any
                        // input, so it can't interfere with editing. A row
                        // with no saved counterpart (just added, or whose
                        // category/key were edited) shows nothing.
                        <span
                          className={`shrink-0 text-[length:var(--text-2xs)] font-medium uppercase tracking-wide ${confidenceColor(confidence)}`}
                        >
                          {confidence}
                        </span>
                      );
                      const removeButton = (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveFactRow(row.uid)}
                          aria-label={`Remove ${factRowName(row)}`}
                          tooltip="Remove"
                        >
                          <X size={12} />
                        </Button>
                      );
                      // FRONT-1397 fix: a value with an embedded line break (or
                      // long enough that a single-line field would hide most
                      // of it — e.g. a Work container's notes, stored as one
                      // long fact value) gets a multi-line control instead, so
                      // editing it never silently flattens its line breaks.
                      // Ordinary rows keep the exact single-line layout as
                      // before.
                      if (isMultilineValue(row.value)) {
                        return (
                          <div key={row.uid} className="flex flex-col gap-1">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-1.5">
                                <Input
                                  value={row.category}
                                  onChange={(e) => handleUpdateFactRow(row.uid, { category: e.target.value })}
                                  placeholder="employment"
                                  aria-label="Fact category"
                                  aria-invalid={!!rowErrors?.category}
                                  className="h-8 w-24 shrink-0 text-xs font-mono"
                                />
                                <Input
                                  value={row.key}
                                  onChange={(e) => handleUpdateFactRow(row.uid, { key: e.target.value })}
                                  placeholder="role"
                                  aria-label="Fact key"
                                  aria-invalid={!!rowErrors?.key}
                                  className="h-8 w-28 shrink-0 text-xs font-mono"
                                />
                                {confidenceMarker}
                                {removeButton}
                              </div>
                              <Textarea
                                value={row.value}
                                onChange={(e) => handleUpdateFactRow(row.uid, { value: e.target.value })}
                                placeholder="Value"
                                aria-label="Fact value"
                                aria-invalid={!!rowErrors?.value}
                                className="min-h-20 w-full text-xs"
                              />
                            </div>
                            {rowMessage && <p className="text-[length:var(--text-2xs)] text-destructive pl-0.5">{rowMessage}</p>}
                          </div>
                        );
                      }
                      return (
                        <div key={row.uid} className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <Input
                              value={row.category}
                              onChange={(e) => handleUpdateFactRow(row.uid, { category: e.target.value })}
                              placeholder="employment"
                              aria-label="Fact category"
                              aria-invalid={!!rowErrors?.category}
                              className="h-8 w-24 shrink-0 text-xs font-mono"
                            />
                            <Input
                              value={row.key}
                              onChange={(e) => handleUpdateFactRow(row.uid, { key: e.target.value })}
                              placeholder="role"
                              aria-label="Fact key"
                              aria-invalid={!!rowErrors?.key}
                              className="h-8 w-28 shrink-0 text-xs font-mono"
                            />
                            <Input
                              value={row.value}
                              onChange={(e) => handleUpdateFactRow(row.uid, { value: e.target.value })}
                              placeholder="CTO"
                              aria-label="Fact value"
                              aria-invalid={!!rowErrors?.value}
                              className="h-8 flex-1 text-xs"
                            />
                            {confidenceMarker}
                            {removeButton}
                          </div>
                          {rowMessage && <p className="text-[length:var(--text-2xs)] text-destructive pl-0.5">{rowMessage}</p>}
                        </div>
                      );
                    })}
                  </div>
                  <Button size="xs" variant="ghost" className="self-start" onClick={handleAddFactRow}>
                    <Plus size={14} /> Add fact
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'relationships' && (
          <div className="flex flex-col gap-2">
            {relationships.length > 0 ? (
              relationships.map((r) => {
                const sourceLabel = r.source_name || r.source_id;
                const targetLabel = r.target_name || r.target_id;
                const sourceIsFocal = r.source_id === entity.id;
                const targetIsFocal = r.target_id === entity.id;
                return (
                  <div key={r.id} className="flex items-center gap-1.5 text-xs flex-wrap">
                    <span className="w-2 h-2 rounded-full bg-chart-2 shrink-0" aria-hidden />
                    <span
                      className={
                        sourceIsFocal
                          ? 'text-muted-foreground'
                          : 'font-semibold text-foreground'
                      }
                    >
                      {sourceLabel}
                    </span>
                    <span className="text-muted-foreground/70" aria-hidden>→</span>
                    <span className="text-muted-foreground italic">{r.relation}</span>
                    <span className="text-muted-foreground/70" aria-hidden>→</span>
                    <span
                      className={
                        targetIsFocal
                          ? 'text-muted-foreground'
                          : 'font-semibold text-foreground'
                      }
                    >
                      {targetLabel}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-muted-foreground">No relationships</div>
            )}
          </div>
        )}

        {tab === 'provenance' && (
          <div className="flex flex-col gap-3">
            {/* Entity-level origin (FRONT-377). */}
            {liveEntity.provenance?.surface ? (
              <div className="rounded-md border border-border/60 bg-muted/30 p-2 text-xs">
                <div className="text-foreground">
                  Learned via{' '}
                  <span className="font-medium">{surfaceLabel(liveEntity.provenance.surface)}</span>
                  {liveEntity.created_at ? <> · {relativeTime(liveEntity.created_at)}</> : null}
                </div>
                {liveEntity.provenance.sessionId ? (
                  (liveEntity.provenance.surface === 'chat' ||
                    liveEntity.provenance.surface === 'document') &&
                  onOpenSourceChat ? (
                    <Button
                      variant="link"
                      size="xs"
                      className="mt-0.5 h-auto justify-start px-0 text-xs"
                      onClick={() =>
                        onOpenSourceChat(liveEntity.provenance!.sessionId!, liveEntity.name)
                      }
                    >
                      Open source chat
                    </Button>
                  ) : (
                    <div className="mt-0.5 text-muted-foreground">
                      Session <span className="font-mono">{liveEntity.provenance.sessionId.slice(0, 8)}</span>
                    </div>
                  )
                ) : null}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">Origin not recorded for this entity.</div>
            )}

            {/* Per-attribute origin. */}
            {attributes.length > 0 && (
              <div className="flex flex-col gap-2">
                {attributes.map((a) => (
                  <AttributeOriginRow key={`${a.category}.${a.key}.${a.learned_at}`} attribute={a} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="px-4 py-3 border-t border-border flex flex-wrap gap-2">
        <Button
          size="xs"
          variant={isPinned ? 'default' : 'outline'}
          onClick={() => pinMutation.mutate(!isPinned)}
          disabled={pinMutation.isPending}
          className="transition-colors"
        >
          {pinMutation.isPending ? (
            <CircleNotch size={12} className="animate-spin" />
          ) : (
            <PushPin size={12} weight={isPinned ? 'fill' : 'regular'} />
          )}{' '}
          {isPinned ? 'Pinned' : 'Pin'}
        </Button>
        <Button
          size="xs"
          variant={isFlagged ? 'destructive' : 'outline'}
          onClick={() => flagMutation.mutate(!isFlagged)}
          disabled={flagMutation.isPending}
          className="transition-colors"
        >
          {flagMutation.isPending ? (
            <CircleNotch size={12} className="animate-spin" />
          ) : (
            <Flag size={12} weight={isFlagged ? 'fill' : 'regular'} />
          )}{' '}
          {isFlagged ? 'Flagged' : 'Flag'}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="xs" variant="outline">
              <MagnifyingGlass size={12} /> Research <CaretDown size={10} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {/* Both depths are gated behind a plan card the user approves —
                same as any research ask. Saying "runs in the background" alone
                promised work that had not started yet, so each line names the
                confirm step. */}
            <DropdownMenuItem onSelect={() => handleResearch('standard')}>
              <div className="flex flex-col gap-0.5">
                <span>Research</span>
                <span className="text-xs text-muted-foreground">
                  A quick pass, answered here once you confirm.
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => handleResearch('deep')}>
              <div className="flex flex-col gap-0.5">
                <span>Deep research</span>
                <span className="text-xs text-muted-foreground">
                  A sourced briefing, run in the background once you confirm.
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {liveEntity.archived_at ? (
          <Button
            size="xs"
            variant="outline"
            onClick={() => archiveEntity.mutate({ id: entity.id, archived: false })}
            disabled={archiveEntity.isPending}
          >
            {archiveEntity.isPending ? (
              <CircleNotch size={12} className="animate-spin" />
            ) : (
              <ArrowCounterClockwise size={12} />
            )}{' '}
            Reactivate
          </Button>
        ) : (
          /* FRONT-1216: the mirror of Reactivate, for every kind of entry.
             Projects could be set aside from the Work card menu; people,
             companies, events and preferences had no route at all. */
          <Button
            size="xs"
            variant="outline"
            onClick={() => archiveEntity.mutate({ id: entity.id, archived: true })}
            disabled={archiveEntity.isPending}
          >
            {archiveEntity.isPending ? (
              <CircleNotch size={12} className="animate-spin" />
            ) : (
              <Archive size={12} />
            )}{' '}
            Set inactive
          </Button>
        )}
        {!hideForget && (
          <Button
            size="xs"
            variant="ghost"
            className="text-destructive hover:text-destructive ml-auto"
            onClick={handleDelete}
            disabled={deleteEntity.isPending}
          >
            <Eraser size={12} /> Forget
          </Button>
        )}
      </div>

      {/* FRONT-1397: Save/Discard for the Overview tab's edit draft (name,
          also known as, facts) — outside the scrolling body and below the
          action row above, so it stays reachable however long the fact list
          gets. Overview-only: the other tabs are read-only. Gated on `seeded`
          too (BLOCKER fix) — there's nothing real to save/discard while the
          entry is still loading or failed to load, since the draft shown then
          is just the blank placeholder. Renders nothing until `dirty` beyond
          that — do not wrap it in another `dirty &&` guard. */}
      {tab === 'overview' && seeded && (
        <ActionBar
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={hasFactErrors || nameEmpty}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}
    </aside>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function confidenceColor(c: 'low' | 'medium' | 'high'): string {
  if (c === 'high') return 'text-success';
  if (c === 'low') return 'text-amber-600 dark:text-amber-400';
  return 'text-foreground';
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Date.now() - t;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

